'use client';

// BILL-20a (D19) — the global 241 toast: sonner's toast.custom rendering
// PlanUpsell, so the toast reads the price through the same hook as every
// in-page upsell. Two callers, one toast: apiFetch's registered 241 handler
// (dashboard layout) and toastStore.showError (WS ErrorEvent, or a caller
// re-surfacing an AuthError's wire). Both key the toast on the feature, so
// sonner replaces instead of stacking (the showDemoInvitation precedent), and
// the toastStore mirror is de-duplicated the same way.
import { ErrorBoundary } from 'react-error-boundary';
import { toast } from 'sonner';
import type { WireError } from '@/lib/errors/catalog';
import { translateImperative } from '@/lib/i18n/imperative';
import { useToastStore } from '@/stores/toastStore';
import { PlanUpsell } from './PlanUpsell';
import { ensureSubscriptionPlans, getSubscriptionPlansSnapshot } from './useBillingData';

const PLAN_UPSELL_TOAST_DURATION_MS = 10_000;

/** The sonner + store id for one feature's upsell toast. */
export function planUpsellToastId(feature: string | undefined): string {
  return `plan-upsell:${feature || 'unknown'}`;
}

export function showPlanUpsellToast(wire: WireError): void {
  const id = planUpsellToastId(wire.feature);
  // D18: nobody has asked for the catalog yet — start the shared fetch now so
  // the toast's PlanUpsell joins it rather than rendering priceless.
  if (getSubscriptionPlansSnapshot().state === 'never_fetched') void ensureSubscriptionPlans();
  const message =
    translateImperative('errors.PLAN_UPGRADE_REQUIRED') ?? 'This is part of a higher plan.';
  toast.custom(
    (toastId) => (
      // The Toaster sits at the app root: a render error here must not take
      // the whole tree down. The fallback is the plain refusal line.
      <ErrorBoundary
        fallback={
          <div
            role="status"
            className="w-[356px] max-w-full rounded-lg border border-border bg-surface p-4 text-sm text-foreground shadow-lg"
          >
            {message}
          </div>
        }
      >
        <PlanUpsell
          variant="toast"
          feature={wire.feature}
          requiredTier={wire.requiredTier}
          onNavigate={() => toast.dismiss(toastId)}
        />
      </ErrorBoundary>
    ),
    { id, duration: PLAN_UPSELL_TOAST_DURATION_MS },
  );
  // Mirror into the store by id, or the in-app list re-grows the stack the
  // toast layer just de-duplicated (showDemoInvitation precedent).
  useToastStore.setState((state) => {
    if (!state.toasts.some((t) => t.id === id)) state.toasts.push({ id, type: 'info', message });
  });
}
